import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    Layers, CalendarCheck, Zap, Plus, Search, Edit2, Trash2, 
    CheckCircle2, Clock, Sparkles, Ticket, 
    Tag, X, AlertCircle
} from 'lucide-react';
import api from '../services/api';
import useAuthStore from '../store/authStore';

export interface ForfaitAbonnement {
    id: number;
    name: string;
    price: string | number;
    registration_fee?: string | number;
    periodicity: 'mensuel' | 'trimestriel' | 'semestriel' | 'annuel' | 'hebdomadaire' | 'autre';
    duration: string;
    description?: string;
    with_coach?: boolean | number;
    max_members?: number;
    active: boolean | number;
    created_at?: string;
}

export interface ForfaitSeance {
    id: number;
    name: string;
    price: string | number;
    registration_fee?: string | number;
    description?: string;
    with_coach?: boolean | number;
    active: boolean | number;
    created_at?: string;
}

const PERIODICITY_LABELS: Record<string, string> = {
    mensuel: 'Mensuel',
    trimestriel: 'Trimestriel (3 mois)',
    semestriel: 'Semestriel (6 mois)',
    annuel: 'Annuel (1 an)',
    hebdomadaire: 'Hebdomadaire (1 semaine)',
    autre: 'Autre périodicité'
};

const Forfaits = () => {
    const user = useAuthStore(state => state.user);
    const isAdmin = !user || user.role === 'admin' || user.role === 'caissier';
    const [searchParams, setSearchParams] = useSearchParams();

    // Active sub-tab: 'abonnements' | 'seances'
    const initialTab = searchParams.get('tab') === 'seances' ? 'seances' : 'abonnements';
    const [activeTab, setActiveTab] = useState<'abonnements' | 'seances'>(initialTab);

    // Sync tab with URL
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'seances' || tabParam === 'abonnements') {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    const handleTabChange = (tab: 'abonnements' | 'seances') => {
        setActiveTab(tab);
        setSearchParams({ tab });
        setSearchTerm('');
    };

    // Data lists
    const [abonnements, setAbonnements] = useState<ForfaitAbonnement[]>([]);
    const [seances, setSeances] = useState<ForfaitSeance[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

    // Modals
    const [isAbonnementModalOpen, setIsAbonnementModalOpen] = useState(false);
    const [isSeanceModalOpen, setIsSeanceModalOpen] = useState(false);
    const [editingAbonnement, setEditingAbonnement] = useState<ForfaitAbonnement | null>(null);
    const [editingSeance, setEditingSeance] = useState<ForfaitSeance | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Abonnement Form state
    const [abonnementForm, setAbonnementForm] = useState({
        name: '',
        price: '',
        registration_fee: '',
        periodicity: 'mensuel' as 'mensuel' | 'trimestriel' | 'semestriel' | 'annuel' | 'hebdomadaire' | 'autre',
        duration: '1 Mois',
        description: '',
        with_coach: false,
        max_members: 1 as number | string,
        active: true
    });

    // Seance Form state
    const [seanceForm, setSeanceForm] = useState({
        name: '',
        price: '',
        registration_fee: '',
        description: '',
        with_coach: false,
        active: true
    });

    // Notification toast
    const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const showToast = (text: string, type: 'success' | 'error' = 'success') => {
        setToastMessage({ text, type });
        setTimeout(() => setToastMessage(null), 3500);
    };

    // Fetch data
    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [resAb, resSc] = await Promise.all([
                api.get('/forfaits/abonnements'),
                api.get('/forfaits/seances')
            ]);
            setAbonnements(resAb.data);
            setSeances(resSc.data);
        } catch (error) {
            console.error('Erreur chargement forfaits:', error);
            showToast('Erreur lors du chargement des forfaits', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Filtered lists
    const filteredAbonnements = useMemo(() => {
        return abonnements.filter(item => {
            const matchesSearch = 
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                item.duration.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.periodicity.toLowerCase().includes(searchTerm.toLowerCase());
            
            const isActive = Boolean(item.active);
            if (filterActive === 'active') return matchesSearch && isActive;
            if (filterActive === 'inactive') return matchesSearch && !isActive;
            return matchesSearch;
        });
    }, [abonnements, searchTerm, filterActive]);

    const filteredSeances = useMemo(() => {
        return seances.filter(item => {
            const matchesSearch = 
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const isActive = Boolean(item.active);
            if (filterActive === 'active') return matchesSearch && isActive;
            if (filterActive === 'inactive') return matchesSearch && !isActive;
            return matchesSearch;
        });
    }, [seances, searchTerm, filterActive]);

    // Handle Open Modals
    const handleOpenCreateAbonnement = () => {
        setEditingAbonnement(null);
        setAbonnementForm({
            name: '',
            price: '',
            registration_fee: '',
            periodicity: 'mensuel',
            duration: '1 Mois',
            description: '',
            with_coach: false,
            max_members: 1,
            active: true
        });
        setIsAbonnementModalOpen(true);
    };

    const handleOpenEditAbonnement = (item: ForfaitAbonnement) => {
        setEditingAbonnement(item);
        setAbonnementForm({
            name: item.name,
            price: item.price.toString(),
            registration_fee: item.registration_fee !== undefined && item.registration_fee !== null ? item.registration_fee.toString() : '',
            periodicity: item.periodicity,
            duration: item.duration,
            description: item.description || '',
            with_coach: Boolean(item.with_coach),
            max_members: item.max_members || 1,
            active: Boolean(item.active)
        });
        setIsAbonnementModalOpen(true);
    };

    const handleOpenCreateSeance = () => {
        setEditingSeance(null);
        setSeanceForm({
            name: '',
            price: '',
            registration_fee: '',
            description: '',
            with_coach: false,
            active: true
        });
        setIsSeanceModalOpen(true);
    };

    const handleOpenEditSeance = (item: ForfaitSeance) => {
        setEditingSeance(item);
        setSeanceForm({
            name: item.name,
            price: item.price.toString(),
            registration_fee: item.registration_fee !== undefined && item.registration_fee !== null ? item.registration_fee.toString() : '',
            description: item.description || '',
            with_coach: Boolean(item.with_coach),
            active: Boolean(item.active)
        });
        setIsSeanceModalOpen(true);
    };

    // Submit Abonnement
    const handleSubmitAbonnement = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!abonnementForm.name.trim()) {
            showToast('Le nom du forfait est requis', 'error');
            return;
        }
        if (!abonnementForm.price || isNaN(parseFloat(abonnementForm.price))) {
            showToast('Un montant valide est requis', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                name: abonnementForm.name.trim(),
                price: parseFloat(abonnementForm.price),
                registration_fee: abonnementForm.registration_fee ? parseFloat(abonnementForm.registration_fee) : 0,
                periodicity: abonnementForm.periodicity,
                duration: abonnementForm.duration.trim(),
                description: abonnementForm.description.trim(),
                with_coach: abonnementForm.with_coach,
                max_members: Number(abonnementForm.max_members) > 0 ? Number(abonnementForm.max_members) : 1,
                active: abonnementForm.active
            };

            if (editingAbonnement) {
                await api.put(`/forfaits/abonnements/${editingAbonnement.id}`, payload);
                showToast('Forfait d\'abonnement modifié avec succès');
            } else {
                await api.post('/forfaits/abonnements', payload);
                showToast('Nouveau forfait d\'abonnement créé avec succès');
            }
            setIsAbonnementModalOpen(false);
            fetchAllData();
        } catch (error: any) {
            console.error('Erreur sauvegarde abonnement:', error);
            showToast(error.response?.data?.error || 'Erreur lors de l\'enregistrement', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Submit Seance
    const handleSubmitSeance = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!seanceForm.name.trim()) {
            showToast('Le nom de la séance est requis', 'error');
            return;
        }
        if (!seanceForm.price || isNaN(parseFloat(seanceForm.price))) {
            showToast('Un montant valide est requis', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                name: seanceForm.name.trim(),
                price: parseFloat(seanceForm.price),
                registration_fee: seanceForm.registration_fee ? parseFloat(seanceForm.registration_fee) : 0,
                description: seanceForm.description.trim(),
                with_coach: seanceForm.with_coach,
                active: seanceForm.active
            };

            if (editingSeance) {
                await api.put(`/forfaits/seances/${editingSeance.id}`, payload);
                showToast('Forfait séance modifié avec succès');
            } else {
                await api.post('/forfaits/seances', payload);
                showToast('Nouveau forfait séance créé avec succès');
            }
            setIsSeanceModalOpen(false);
            fetchAllData();
        } catch (error: any) {
            console.error('Erreur sauvegarde séance:', error);
            showToast(error.response?.data?.error || 'Erreur lors de l\'enregistrement', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Toggle Active
    const handleToggleActiveAbonnement = async (item: ForfaitAbonnement) => {
        try {
            const newStatus = !item.active;
            await api.put(`/forfaits/abonnements/${item.id}`, { active: newStatus });
            setAbonnements(prev => prev.map(a => a.id === item.id ? { ...a, active: newStatus } : a));
            showToast(`Forfait ${newStatus ? 'activé' : 'désactivé'}`);
        } catch (error) {
            console.error(error);
            showToast('Impossible de modifier le statut', 'error');
        }
    };

    const handleToggleActiveSeance = async (item: ForfaitSeance) => {
        try {
            const newStatus = !item.active;
            await api.put(`/forfaits/seances/${item.id}`, { active: newStatus });
            setSeances(prev => prev.map(s => s.id === item.id ? { ...s, active: newStatus } : s));
            showToast(`Forfait séance ${newStatus ? 'activé' : 'désactivé'}`);
        } catch (error) {
            console.error(error);
            showToast('Impossible de modifier le statut', 'error');
        }
    };

    // Delete handlers
    const handleDeleteAbonnement = async (id: number, name: string) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement le forfait d'abonnement "${name}" ?`)) {
            return;
        }
        try {
            await api.delete(`/forfaits/abonnements/${id}`);
            setAbonnements(prev => prev.filter(a => a.id !== id));
            showToast('Forfait abonnement supprimé');
        } catch (error) {
            console.error(error);
            showToast('Erreur lors de la suppression', 'error');
        }
    };

    const handleDeleteSeance = async (id: number, name: string) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement le forfait séance "${name}" ?`)) {
            return;
        }
        try {
            await api.delete(`/forfaits/seances/${id}`);
            setSeances(prev => prev.filter(s => s.id !== id));
            showToast('Forfait séance supprimé');
        } catch (error) {
            console.error(error);
            showToast('Erreur lors de la suppression', 'error');
        }
    };

    // Auto duration suggestions when periodicity changes
    const handlePeriodicityChange = (periodicity: ForfaitAbonnement['periodicity']) => {
        let suggestedDuration = abonnementForm.duration;
        switch (periodicity) {
            case 'mensuel':
                suggestedDuration = '1 Mois';
                break;
            case 'trimestriel':
                suggestedDuration = '3 Mois';
                break;
            case 'semestriel':
                suggestedDuration = '6 Mois';
                break;
            case 'annuel':
                suggestedDuration = '1 An';
                break;
            case 'hebdomadaire':
                suggestedDuration = '1 Semaine (7 jours)';
                break;
            default:
                break;
        }
        setAbonnementForm(prev => ({
            ...prev,
            periodicity,
            duration: suggestedDuration
        }));
    };

    // Key Stats calculations
    const activeAbCount = abonnements.filter(a => a.active).length;
    const activeScCount = seances.filter(s => s.active).length;

    return (
        <div className="w-full space-y-6 pb-12">
            {/* Toast Notification */}
            {toastMessage && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl text-white font-medium text-sm transition-all transform animate-bounce ${
                    toastMessage.type === 'success' ? 'bg-black border-2 border-brand-primary' : 'bg-red-600'
                }`}>
                    {toastMessage.type === 'success' ? <CheckCircle2 className="text-brand-primary" size={20} /> : <AlertCircle size={20} />}
                    <span>{toastMessage.text}</span>
                </div>
            )}

            {/* Header with Title and Action Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-gray-900 via-brand-dark to-gray-900 p-6 rounded-2xl text-white shadow-lg border border-gray-800">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-primary/20 border border-brand-primary/40 flex items-center justify-center text-brand-primary">
                            {activeTab === 'abonnements' ? <CalendarCheck size={24} /> : <Zap size={24} />}
                        </div>
                        <div>
                            <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-2">
                                {activeTab === 'abonnements' ? (
                                    <>Forfaits <span className="text-brand-primary">Abonnements</span></>
                                ) : (
                                    <>Forfaits <span className="text-brand-primary">Séances</span></>
                                )}
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-400">
                                {activeTab === 'abonnements'
                                    ? 'Configuration et tarification des formules d\'abonnements récurrents'
                                    : 'Configuration et tarification des tickets et forfaits de séances uniques'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                    {isAdmin && (
                        activeTab === 'abonnements' ? (
                            <button
                                onClick={handleOpenCreateAbonnement}
                                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-primary hover:bg-yellow-400 text-brand-dark font-bold rounded-xl shadow-md transition-all hover:scale-105 w-full sm:w-auto cursor-pointer"
                            >
                                <Plus size={18} strokeWidth={2.5} />
                                <span>Nouveau Forfait Abonnement</span>
                            </button>
                        ) : (
                            <button
                                onClick={handleOpenCreateSeance}
                                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-primary hover:bg-yellow-400 text-brand-dark font-bold rounded-xl shadow-md transition-all hover:scale-105 w-full sm:w-auto cursor-pointer"
                            >
                                <Plus size={18} strokeWidth={2.5} />
                                <span>Nouveau Forfait Séance</span>
                            </button>
                        )
                    )}
                </div>
            </div>

            {/* Quick Stats Cards filtrées selon l'onglet actif */}
            {activeTab === 'abonnements' ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                            <CalendarCheck size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 font-medium">Total Forfaits Abonnements</p>
                            <p className="text-2xl font-black text-brand-dark">{abonnements.length}</p>
                            <span className="text-xs text-green-600 font-semibold">{activeAbCount} actifs</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                            <Sparkles size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 font-medium">Abonnement à partir de</p>
                            <p className="text-2xl font-black text-brand-dark">
                                {abonnements.length > 0 
                                    ? `${Math.min(...abonnements.map(a => Number(a.price))).toLocaleString()} F`
                                    : '-'}
                            </p>
                            <span className="text-xs text-gray-400">Tarif périodique</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <Tag size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 font-medium">Périodicités disponibles</p>
                            <p className="text-2xl font-black text-brand-dark">
                                {new Set(abonnements.map(a => a.periodicity)).size}
                            </p>
                            <span className="text-xs text-gray-400">Mensuel, Trimestriel, etc.</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                            <Zap size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 font-medium">Total Forfaits Séances</p>
                            <p className="text-2xl font-black text-brand-dark">{seances.length}</p>
                            <span className="text-xs text-green-600 font-semibold">{activeScCount} actives</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <Tag size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 font-medium">Séance à partir de</p>
                            <p className="text-2xl font-black text-brand-dark">
                                {seances.length > 0 
                                    ? `${Math.min(...seances.map(s => Number(s.price))).toLocaleString()} F`
                                    : '-'}
                            </p>
                            <span className="text-xs text-gray-400">Tarif unitaire</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                            <Ticket size={24} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 font-medium">Formules sans engagement</p>
                            <p className="text-2xl font-black text-brand-dark">{activeScCount}</p>
                            <span className="text-xs text-gray-400">Accès journée / unitaire</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Container */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header d'onglet spécifique */}
                <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {activeTab === 'abonnements' ? (
                            <span className="flex items-center gap-2 font-bold text-gray-800 text-base">
                                <CalendarCheck size={20} className="text-brand-dark" />
                                <span>Liste des Forfaits Abonnements</span>
                                <span className="bg-brand-primary text-brand-dark px-2.5 py-0.5 rounded-full text-xs font-black">
                                    {abonnements.length}
                                </span>
                            </span>
                        ) : (
                            <span className="flex items-center gap-2 font-bold text-gray-800 text-base">
                                <Zap size={20} className="text-blue-600" />
                                <span>Liste des Forfaits Séances</span>
                                <span className="bg-blue-600 text-white px-2.5 py-0.5 rounded-full text-xs font-black">
                                    {seances.length}
                                </span>
                            </span>
                        )}
                    </div>
                </div>

                {/* Filters & Search Toolbar */}
                <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center bg-white">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder={activeTab === 'abonnements' ? 'Rechercher un forfait abonnement (nom, durée, périodicité)...' : 'Rechercher un forfait séance...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:bg-white transition-all"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="flex items-center bg-gray-100 p-1 rounded-xl text-xs font-medium text-gray-600">
                            <button
                                onClick={() => setFilterActive('all')}
                                className={`px-3 py-1.5 rounded-lg transition-all ${filterActive === 'all' ? 'bg-white text-brand-dark font-bold shadow-xs' : 'hover:text-black'}`}
                            >
                                Tous
                            </button>
                            <button
                                onClick={() => setFilterActive('active')}
                                className={`px-3 py-1.5 rounded-lg transition-all ${filterActive === 'active' ? 'bg-white text-green-700 font-bold shadow-xs' : 'hover:text-black'}`}
                            >
                                Actifs
                            </button>
                            <button
                                onClick={() => setFilterActive('inactive')}
                                className={`px-3 py-1.5 rounded-lg transition-all ${filterActive === 'inactive' ? 'bg-white text-red-700 font-bold shadow-xs' : 'hover:text-black'}`}
                            >
                                Inactifs
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content View */}
                {loading ? (
                    <div className="py-20 text-center">
                        <div className="inline-block w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="mt-4 text-sm text-gray-500 font-medium">Chargement des forfaits en cours...</p>
                    </div>
                ) : activeTab === 'abonnements' ? (
                    /* ================= TAB 1: ABONNEMENTS ================= */
                    filteredAbonnements.length === 0 ? (
                        <div className="py-16 text-center px-4">
                            <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-amber-100">
                                <CalendarCheck size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Aucun forfait d'abonnement trouvé</h3>
                            <p className="text-sm text-gray-500 max-w-md mx-auto mt-1">
                                {searchTerm ? 'Aucun résultat ne correspond à votre recherche.' : 'Créez votre premier forfait d\'abonnement pour commencer à souscrire des clients.'}
                            </p>
                            {isAdmin && !searchTerm && (
                                <button
                                    onClick={handleOpenCreateAbonnement}
                                    className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-brand-dark font-bold rounded-xl text-sm hover:bg-yellow-400 transition-colors shadow-sm"
                                >
                                    <Plus size={16} />
                                    Créer un forfait d'abonnement
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredAbonnements.map((forfait) => (
                                <div
                                    key={forfait.id}
                                    className={`relative flex flex-col justify-between rounded-2xl border transition-all duration-200 hover:shadow-lg ${
                                        forfait.active 
                                            ? 'bg-white border-gray-200 hover:border-brand-primary/50' 
                                            : 'bg-gray-50/60 border-gray-200 opacity-75'
                                    }`}
                                >
                                    {/* Top Status & Duration Bar */}
                                    <div className="p-5 pb-3">
                                        <div className="flex items-start justify-between gap-2 mb-3">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                                forfait.active 
                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                                                    : 'bg-gray-200 text-gray-700'
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${forfait.active ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                                                {forfait.active ? 'Actif' : 'Inactif'}
                                            </span>

                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold">
                                                <Clock size={13} className="text-gray-500" />
                                                <span>Durée: {forfait.duration}</span>
                                            </div>
                                        </div>

                                        {/* Forfait Name */}
                                        <h3 className="text-lg font-black text-gray-900 tracking-tight leading-snug">
                                            {forfait.name}
                                        </h3>

                                        {/* Periodicity Badge */}
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-amber-50 text-amber-800 text-xs font-medium border border-amber-100">
                                                <span>Périodicité :</span>
                                                <strong className="capitalize">{PERIODICITY_LABELS[forfait.periodicity] || forfait.periodicity}</strong>
                                            </div>
                                            {Boolean(forfait.with_coach) && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-amber-100 text-amber-900 text-xs font-bold border border-amber-300">
                                                    🏋️ Avec Coach
                                                </span>
                                            )}
                                            {Boolean(forfait.max_members && Number(forfait.max_members) > 1) && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-purple-100 text-purple-900 text-xs font-bold border border-purple-300">
                                                    👥 Pack {forfait.max_members} Personnes (1 badge)
                                                </span>
                                            )}
                                        </div>

                                        {/* Price display */}
                                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-baseline gap-1">
                                            <span className="text-2xl font-black text-gray-900 tracking-tight">
                                                {Number(forfait.price).toLocaleString()}
                                            </span>
                                            <span className="text-xs font-bold text-amber-600 uppercase">FCFA</span>
                                            <span className="text-xs text-gray-400 ml-1">/ {forfait.duration}</span>
                                        </div>

                                        {/* Inscription Fee Badge */}
                                        {Number(forfait.registration_fee) > 0 ? (
                                            <div className="mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100 flex items-center justify-between">
                                                <span>Frais d'inscription :</span>
                                                <strong className="font-black">+{Number(forfait.registration_fee).toLocaleString()} FCFA</strong>
                                            </div>
                                        ) : (
                                            <div className="mt-2 text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-md border border-gray-100">
                                                <span>Sans frais d'inscription (0 FCFA)</span>
                                            </div>
                                        )}

                                        {/* Description */}
                                        <p className="mt-3 text-xs sm:text-sm text-gray-600 line-clamp-3 min-h-[40px]">
                                            {forfait.description || 'Aucune description détaillée.'}
                                        </p>
                                    </div>

                                    {/* Action Buttons Footer */}
                                    <div className="p-4 pt-3 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl flex items-center justify-between gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleToggleActiveAbonnement(forfait)}
                                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                                                forfait.active 
                                                    ? 'text-gray-600 border-gray-200 hover:bg-gray-100' 
                                                    : 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                                            }`}
                                        >
                                            {forfait.active ? 'Désactiver' : 'Activer'}
                                        </button>

                                        {isAdmin && (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEditAbonnement(forfait)}
                                                    className="p-1.5 text-gray-500 hover:text-brand-dark hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all cursor-pointer"
                                                    title="Modifier le forfait"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteAbonnement(forfait.id, forfait.name)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 transition-all cursor-pointer"
                                                    title="Supprimer le forfait"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    /* ================= TAB 2: SEANCES ================= */
                    filteredSeances.length === 0 ? (
                        <div className="py-16 text-center px-4">
                            <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-100">
                                <Zap size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Aucun forfait séance trouvé</h3>
                            <p className="text-sm text-gray-500 max-w-md mx-auto mt-1">
                                {searchTerm ? 'Aucun résultat ne correspond à votre recherche.' : 'Créez votre première formule de séance unique pour les passages ponctuels.'}
                            </p>
                            {isAdmin && !searchTerm && (
                                <button
                                    onClick={handleOpenCreateSeance}
                                    className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-brand-dark font-bold rounded-xl text-sm hover:bg-yellow-400 transition-colors shadow-sm"
                                >
                                    <Plus size={16} />
                                    Créer un forfait séance
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredSeances.map((seance) => (
                                <div
                                    key={seance.id}
                                    className={`relative flex flex-col justify-between rounded-2xl border transition-all duration-200 hover:shadow-lg ${
                                        seance.active 
                                            ? 'bg-white border-gray-200 hover:border-blue-400/50' 
                                            : 'bg-gray-50/60 border-gray-200 opacity-75'
                                    }`}
                                >
                                    {/* Top Status & Badge Bar */}
                                    <div className="p-5 pb-3">
                                        <div className="flex items-start justify-between gap-2 mb-3">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                                seance.active 
                                                    ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                                                    : 'bg-gray-200 text-gray-700'
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${seance.active ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
                                                {seance.active ? 'Actif' : 'Inactif'}
                                            </span>

                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50/70 text-blue-700 text-xs font-semibold">
                                                <Ticket size={13} />
                                                <span>Séance unique</span>
                                            </div>
                                        </div>

                                        {/* Seance Name */}
                                        <h3 className="text-lg font-black text-gray-900 tracking-tight leading-snug">
                                            {seance.name}
                                        </h3>

                                        {Boolean(seance.with_coach) && (
                                            <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-amber-100 text-amber-900 text-xs font-bold border border-amber-300">
                                                🏋️ Avec Coach Personnel
                                            </div>
                                        )}

                                        {/* Price display */}
                                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-baseline gap-1">
                                            <span className="text-2xl font-black text-gray-900 tracking-tight">
                                                {Number(seance.price).toLocaleString()}
                                            </span>
                                            <span className="text-xs font-bold text-blue-600 uppercase">FCFA</span>
                                            <span className="text-xs text-gray-400 ml-1">/ séance</span>
                                        </div>

                                        {/* Inscription Fee Badge */}
                                        {Number(seance.registration_fee) > 0 ? (
                                            <div className="mt-2 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100 flex items-center justify-between">
                                                <span>Frais d'inscription :</span>
                                                <strong className="font-black">+{Number(seance.registration_fee).toLocaleString()} FCFA</strong>
                                            </div>
                                        ) : (
                                            <div className="mt-2 text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-md border border-gray-100">
                                                <span>Sans frais d'inscription (0 FCFA)</span>
                                            </div>
                                        )}

                                        {/* Description */}
                                        <p className="mt-3 text-xs sm:text-sm text-gray-600 line-clamp-3 min-h-[40px]">
                                            {seance.description || 'Aucune description fournie.'}
                                        </p>
                                    </div>

                                    {/* Action Buttons Footer */}
                                    <div className="p-4 pt-3 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl flex items-center justify-between gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleToggleActiveSeance(seance)}
                                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                                                seance.active 
                                                    ? 'text-gray-600 border-gray-200 hover:bg-gray-100' 
                                                    : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                                            }`}
                                        >
                                            {seance.active ? 'Désactiver' : 'Activer'}
                                        </button>

                                        {isAdmin && (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEditSeance(seance)}
                                                    className="p-1.5 text-gray-500 hover:text-brand-dark hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all cursor-pointer"
                                                    title="Modifier le forfait séance"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteSeance(seance.id, seance.name)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 transition-all cursor-pointer"
                                                    title="Supprimer le forfait séance"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>

            {/* ========================================================================= */}
            {/* MODAL 1: CRÉER / MODIFIER UN FORFAIT ABONNEMENT */}
            {/* Champs demandés: Nom, Montant, Périodicité, Durée, Description du forfait */}
            {/* ========================================================================= */}
            {isAbonnementModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="px-6 py-4 bg-brand-dark text-white flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-brand-primary/20 text-brand-primary flex items-center justify-center">
                                    <CalendarCheck size={18} />
                                </div>
                                <h2 className="text-lg font-black tracking-tight text-white">
                                    {editingAbonnement ? 'Modifier le Forfait Abonnement' : 'Nouveau Forfait Abonnement'}
                                </h2>
                            </div>
                            <button
                                onClick={() => setIsAbonnementModalOpen(false)}
                                className="text-gray-400 hover:text-white transition-colors p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSubmitAbonnement} className="p-6 space-y-4">
                            {/* Nom du forfait */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Nom du forfait <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ex: Pass Musculation & Cardio Gold"
                                    value={abonnementForm.name}
                                    onChange={(e) => setAbonnementForm({ ...abonnementForm, name: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary focus:bg-white focus:outline-none transition-all"
                                />
                            </div>

                            {/* Montant & Périodicité */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                        Montant Forfait (FCFA) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            required
                                            min="0"
                                            step="500"
                                            placeholder="Ex: 30000"
                                            value={abonnementForm.price}
                                            onChange={(e) => setAbonnementForm({ ...abonnementForm, price: e.target.value })}
                                            className="w-full pl-3.5 pr-14 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-primary focus:bg-white focus:outline-none transition-all"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">
                                            FCFA
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                        Périodicité <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={abonnementForm.periodicity}
                                        onChange={(e) => handlePeriodicityChange(e.target.value as any)}
                                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-primary focus:bg-white focus:outline-none transition-all"
                                    >
                                        <option value="mensuel">Mensuel</option>
                                        <option value="trimestriel">Trimestriel (3 mois)</option>
                                        <option value="semestriel">Semestriel (6 mois)</option>
                                        <option value="annuel">Annuel (1 an)</option>
                                        <option value="hebdomadaire">Hebdomadaire</option>
                                        <option value="autre">Autre</option>
                                    </select>
                                </div>
                            </div>

                            {/* Frais d'inscription (Optionnel / Annuel) */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Montant Inscription Annuelle (FCFA)
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        min="0"
                                        step="500"
                                        placeholder="Ex: 10000 (laisser 0 ou vide si sans frais d'inscription)"
                                        value={abonnementForm.registration_fee}
                                        onChange={(e) => setAbonnementForm({ ...abonnementForm, registration_fee: e.target.value })}
                                        className="w-full pl-3.5 pr-14 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-primary focus:bg-white focus:outline-none transition-all"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">
                                        FCFA
                                    </span>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Si défini, ce montant d'inscription annuelle pourra être ajouté au choix lors de la souscription client.
                                </p>
                            </div>

                            {/* Durée du forfait */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Durée du forfait <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ex: 1 Mois, 3 Mois, 6 Mois, 1 An..."
                                    value={abonnementForm.duration}
                                    onChange={(e) => setAbonnementForm({ ...abonnementForm, duration: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary focus:bg-white focus:outline-none transition-all"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Texte affiché sur les reçus et la fiche client (ex: 1 Mois, 30 jours, 1 An).
                                </p>
                            </div>

                            {/* Description du forfait */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Description du forfait
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Précisez les avantages inclus, conditions d'accès, équipements ou cours compris..."
                                    value={abonnementForm.description}
                                    onChange={(e) => setAbonnementForm({ ...abonnementForm, description: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary focus:bg-white focus:outline-none transition-all resize-none"
                                />
                            </div>

                            {/* Option Associé à un Coach Personnel */}
                            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/70 flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="withCoachAbonnement"
                                    checked={abonnementForm.with_coach}
                                    onChange={(e) => setAbonnementForm({ ...abonnementForm, with_coach: e.target.checked })}
                                    className="w-4 h-4 mt-0.5 text-amber-600 rounded border-gray-300 focus:ring-amber-500 cursor-pointer"
                                />
                                <div>
                                    <label htmlFor="withCoachAbonnement" className="text-xs font-bold text-gray-900 select-none cursor-pointer block">
                                        Forfait avec Coach Personnel (Coaching)
                                    </label>
                                    <p className="text-[11px] text-gray-600 mt-0.5">
                                        Si activé, l'affectation d'un coach sera demandée lors de la souscription d'un abonnement.
                                    </p>
                                </div>
                            </div>

                            {/* Option Pack Multi-personnes (Entreprise / Groupe) */}
                            <div className="p-3.5 rounded-xl border border-purple-200 bg-purple-50/70 space-y-2">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        id="isGroupPackAbonnement"
                                        checked={Number(abonnementForm.max_members) > 1}
                                        onChange={(e) => setAbonnementForm({ ...abonnementForm, max_members: e.target.checked ? 5 : 1 })}
                                        className="w-4 h-4 mt-0.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500 cursor-pointer"
                                    />
                                    <div className="flex-1">
                                        <label htmlFor="isGroupPackAbonnement" className="text-xs font-bold text-gray-900 select-none cursor-pointer block">
                                            👥 Pack Multi-personnes (Pack Entreprise / Groupe)
                                        </label>
                                        <p className="text-[11px] text-gray-600 mt-0.5">
                                            Permet de renseigner plusieurs personnes bénéficiaires pour ce pack, avec 1 seul badge partagé.
                                        </p>
                                    </div>
                                </div>
                                {Number(abonnementForm.max_members) > 1 && (
                                    <div className="pt-2 border-t border-purple-200/60 flex items-center justify-between gap-3">
                                        <span className="text-xs font-bold text-purple-900">
                                            Nombre maximum de personnes autorisées :
                                        </span>
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                type="number"
                                                min="2"
                                                max="50"
                                                value={abonnementForm.max_members}
                                                onChange={(e) => setAbonnementForm({ ...abonnementForm, max_members: Math.max(1, parseInt(e.target.value) || 1) })}
                                                className="w-20 px-2.5 py-1 text-center font-bold text-xs bg-white border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                            <span className="text-xs text-purple-700 font-semibold">personnes</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Statut actif */}
                            <div className="flex items-center gap-2 pt-1">
                                <input
                                    type="checkbox"
                                    id="activeAbonnement"
                                    checked={abonnementForm.active}
                                    onChange={(e) => setAbonnementForm({ ...abonnementForm, active: e.target.checked })}
                                    className="w-4 h-4 text-brand-primary rounded border-gray-300 focus:ring-brand-primary"
                                />
                                <label htmlFor="activeAbonnement" className="text-sm font-medium text-gray-700 select-none cursor-pointer">
                                    Rendre ce forfait d'abonnement actif et disponible immédiatement
                                </label>
                            </div>

                            {/* Modal Actions */}
                            <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsAbonnementModalOpen(false)}
                                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2.5 bg-brand-primary hover:bg-yellow-400 text-brand-dark font-bold text-sm rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-brand-dark border-t-transparent rounded-full animate-spin"></div>
                                            <span>Enregistrement...</span>
                                        </>
                                    ) : (
                                        <span>{editingAbonnement ? 'Mettre à jour' : 'Enregistrer le forfait'}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========================================================================= */}
            {/* MODAL 2: CRÉER / MODIFIER UN FORFAIT SÉANCE */}
            {/* Champs demandés: Nom, Montant, Description du forfait */}
            {/* ========================================================================= */}
            {isSeanceModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="px-6 py-4 bg-brand-dark text-white flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center">
                                    <Zap size={18} />
                                </div>
                                <h2 className="text-lg font-black tracking-tight text-white">
                                    {editingSeance ? 'Modifier le Forfait Séance' : 'Nouveau Forfait Séance'}
                                </h2>
                            </div>
                            <button
                                onClick={() => setIsSeanceModalOpen(false)}
                                className="text-gray-400 hover:text-white transition-colors p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSubmitSeance} className="p-6 space-y-4">
                            {/* Nom de la séance */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Nom de la séance <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ex: Séance Musculation Unique, Pass Journée..."
                                    value={seanceForm.name}
                                    onChange={(e) => setSeanceForm({ ...seanceForm, name: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-all"
                                />
                            </div>

                            {/* Montant & Frais Inscription */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                        Montant Séance (FCFA) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            required
                                            min="0"
                                            step="500"
                                            placeholder="Ex: 5000"
                                            value={seanceForm.price}
                                            onChange={(e) => setSeanceForm({ ...seanceForm, price: e.target.value })}
                                            className="w-full pl-3.5 pr-14 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-all"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">
                                            FCFA
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                        Frais d'inscription (FCFA)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            min="0"
                                            step="500"
                                            placeholder="Ex: 5000 (ou 0)"
                                            value={seanceForm.registration_fee}
                                            onChange={(e) => setSeanceForm({ ...seanceForm, registration_fee: e.target.value })}
                                            className="w-full pl-3.5 pr-14 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-all"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">
                                            FCFA
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Description du forfait */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Description du forfait
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Détails de l'accès ponctuel, équipement autorisé, validité pour la journée..."
                                    value={seanceForm.description}
                                    onChange={(e) => setSeanceForm({ ...seanceForm, description: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-all resize-none"
                                />
                            </div>

                            {/* Option Associé à un Coach Personnel */}
                            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/70 flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="withCoachSeance"
                                    checked={seanceForm.with_coach}
                                    onChange={(e) => setSeanceForm({ ...seanceForm, with_coach: e.target.checked })}
                                    className="w-4 h-4 mt-0.5 text-amber-600 rounded border-gray-300 focus:ring-amber-500 cursor-pointer"
                                />
                                <div>
                                    <label htmlFor="withCoachSeance" className="text-xs font-bold text-gray-900 select-none cursor-pointer block">
                                        Forfait avec Coach Personnel (Coaching)
                                    </label>
                                    <p className="text-[11px] text-gray-600 mt-0.5">
                                        Si activé, l'affectation d'un coach sera demandée lors de la souscription d'une séance.
                                    </p>
                                </div>
                            </div>

                            {/* Statut actif */}
                            <div className="flex items-center gap-2 pt-1">
                                <input
                                    type="checkbox"
                                    id="activeSeance"
                                    checked={seanceForm.active}
                                    onChange={(e) => setSeanceForm({ ...seanceForm, active: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <label htmlFor="activeSeance" className="text-sm font-medium text-gray-700 select-none cursor-pointer">
                                    Rendre ce forfait séance actif et disponible à la vente
                                </label>
                            </div>

                            {/* Modal Actions */}
                            <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsSeanceModalOpen(false)}
                                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2.5 bg-brand-primary hover:bg-yellow-400 text-brand-dark font-bold text-sm rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-brand-dark border-t-transparent rounded-full animate-spin"></div>
                                            <span>Enregistrement...</span>
                                        </>
                                    ) : (
                                        <span>{editingSeance ? 'Mettre à jour' : 'Enregistrer le forfait'}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Forfaits;
